<template>
    <div v-show="isLoaded" class="content-wrapper">
        <div class="common-form-wrapper no-print">
            <div class="wrapper">
                <span v-if="$v.organization.name.$invalid" class="tooltiptext">{{
                    errorDisplay($v.organization.name, { val: MAX_NAME_LENGTH })
                }}</span>
                <div class="input-with-insidelabel" :class="{ tooltiperror: $v.organization.name.$invalid }">
                    <input
                        id="forAT_Organization-Name"
                        v-model="organization.name"
                        class="time-input"
                        @input="$v.organization.name.$touch()"
                    />
                    <label for="forAT_Organization-Name">{{ $t('settings.organizationTab.name') }}</label>
                </div>
            </div>
            <div class="wrapper">
                <span v-if="$v.organization.description.$invalid" class="tooltiptext">{{
                    errorDisplay($v.organization.description, { val: MAX_COMMENT_LENGTH })
                }}</span>
                <div
                    class="textarea-with-insidelabel has-5-rows"
                    :class="{ tooltiperror: $v.organization.description.$invalid }"
                >
                    <textarea
                        id="forAT_Organization-Info"
                        v-model="organization.description"
                        @input="$v.organization.description.$touch()"
                    ></textarea>
                    <label for="forAT_Organization-Info">{{
                        $t('settings.organizationTab.information')
                    }}</label>
                </div>
            </div>
        </div>
    </div>
</template>

<script type="text/babel">
import { mapState } from 'vuex';
import mappingState from '../common/mappingState';
import { maximumNameLength, maximumCommentLength, errorDisplay } from '../../validators';
import { SUCCESS, MAX_NAME_LENGTH, MAX_COMMENT_LENGTH, ROUTE_NAMES } from '../../constants';
import { resourceOrganizationName } from '../../api/resources';
import { getCancelToken } from '../../api';
import { ifIsNotCanceled } from '../common/helpers';

export default {
    mixins: [mappingState],

    constants: {
        MAX_NAME_LENGTH,
        MAX_COMMENT_LENGTH,
    },

    data() {
        return {
            organization: {
                name: '',
                description: '',
            },
            organizationCopy: {},
            cancelToken: getCancelToken(),
            isLoaded: false,
        };
    },

    computed: {
        ...mapState(['isLoading', 'unsavedChanges']),

        /**
         * Checks whether organization data is valid or not
         * @return {Boolean}
         */
        areDataValid() {
            return !this.$v.organization.$invalid;
        },
    },

    watch: {
        organization: {
            handler() {
                this.setUnsavedChanges(true);
            },
            deep: true,
        },
    },

    created() {
        this.setSaveUndoButtons({
            undo: this.undo.bind(this),
            save: this.save.bind(this),
            saveDisabled: () => !this.unsavedChanges || !this.areDataValid,
            wasChanged: () => !this.unsavedChanges,
        });
        this.loadOrgData();
    },

    beforeDestroy() {
        this.cancelToken.cancel();
    },

    validations() {
        return {
            organization: {
                name: { maximumNameLength },
                description: { maximumCommentLength },
            },
        };
    },

    methods: {
        errorDisplay,

        /**
         * Loads organization data from database
         */
        loadOrgData() {
            this.showLoading(true);
            return resourceOrganizationName({
                cancelToken: this.cancelToken.token,
            })
                .then(response => response.data)
                .then(info => {
                    this.organization = info;
                    this.organizationCopy = { ...info };
                    this.setOrganizationName(this.organization.name);
                    this.setContactInfo(this.organization.description);
                    this.$nextTick(() => {
                        this.setUnsavedChanges(false);
                        this.showLoading(false);
                        this.isLoaded = true;
                    });
                })
                .catch(
                    ifIsNotCanceled({
                        callback: response => {
                            this.organizationCopy = { ...this.organization };
                            this.isLoaded = true;
                            return response;
                        },
                    })
                );
        },

        /**
         * Sends organization data into database
         */
        save() {
            if (this.unsavedChanges && this.areDataValid) {
                this.showLoading(true);
                this.organization.name = this.organization.name.trim();
                this.organization.description = this.organization.description.trim();
                return resourceOrganizationName({
                    method: 'post',
                    data: this.organization,
                })
                    .then(() => {
                        if (this.$router.currentRoute.name === ROUTE_NAMES.SETTINGS__ORGANIZATION) {
                            this.popup({
                                type: SUCCESS,
                                header: this.$t('common.success'),
                                text: this.$t('common.saved'),
                            });
                            this.showLoading(false);
                            this.setUnsavedChanges(false);
                            this.setOrganizationName(this.organization.name);
                            this.setContactInfo(this.organization.description);
                            this.organizationCopy = { ...this.organization };
                        }
                    })
                    .catch(ifIsNotCanceled());
            }
        },

        /**
         * Reset organization data to the previous saved data
         */
        undo() {
            this.organization = { ...this.organizationCopy };
            this.$nextTick(() => {
                this.setUnsavedChanges(false);
            });
        },
    },
};
</script>
